import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Container } from 'react-bootstrap'
import './ImageXrayViewer.css'
import { useNavigate, useLocation } from 'react-router-dom'
import { useAppDispatch, useAppSelector } from '@store/hooks'
import { getEnv } from '@utils/env'
import {
  isImageViewerMessageEnvelope,
  IMAGE_VIEWER_MESSAGE_SOURCE,
  type DeleteFileRequestEvent,
  type OhifToPmsMessage,
  type OpenNotesForImageEvent,
  type PmsToOhifMessage,
} from '@utils/imageViewerMessages'
import { NoteContextType } from '@dataTypes/note.type'
import {
  fetchPatientFolder,
  deleteFileAction,
  uploadFileAction,
} from '@store/actions/folder.action'

import { deleteOrphanDicomStudy } from '@services/api/folder.api'
import { getNotificationsSocket } from '@services/websocket/socket'
import ImageViewerModal from '@components/pages/ImageViewer/ImageViewerModal/ImageViewerModal.component'
import ConfirmDeleteModal from '@components/layout/Modals/ConfirmDeleteModal/ConfirmDeleteModal.component'
import FilesPickerModal from '@components/common/FilesPickerModal'
import type { MockFileItem } from '@pages/Files/mockFilesData'
import { useNotificationToast } from '@context/NotificationToastContext'
import {
  createFailedToast,
  createSuccessToast,
} from '@utils/notificationToast.util'
import { useGlobalLoadingActions } from '@hooks/useGlobalLoading'
import { usePublishActivePatient } from '@hooks/usePublishActivePatient'
import OperatoryChairPicker from '@components/common/OperatoryChairPicker/OperatoryChairPicker.component'
import { ScreenLoader } from '@components/ui'

const MAX_FILE_SIZE_BYTES = 100 * 1024 * 1024 // 100 MB
const ACCEPTED_EXTENSIONS = ['.dcm', '.dicom', '.jpg', '.jpeg', '.png']
const XRAY_VIEWER_LOADING_CONTAINER = '.xray-viewer-container'

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

function validateFile(file: File): string | null {
  const ext = file.name.toLowerCase().substring(file.name.lastIndexOf('.'))
  if (!ACCEPTED_EXTENSIONS.includes(ext)) {
    return `"${file.name}" is not a supported file type. Accepted: ${ACCEPTED_EXTENSIONS.join(', ')}`
  }
  if (file.size > MAX_FILE_SIZE_BYTES) {
    return `"${file.name}" exceeds the 100 MB size limit (${formatFileSize(file.size)})`
  }
  return null
}

function useXrayViewerBridge(
  iframeRef: React.RefObject<HTMLIFrameElement | null>,
  ohifUrl: string,
  callbacks?: {
    onPrintReport?: () => void
    onAddFiles?: () => void
    onOpenCloudStorage?: () => void
    onDeleteFileRequest?: (payload: DeleteFileRequestEvent['payload']) => void
    onRequestFileMetadata?: () => void
  },
): { sendCommand: (message: PmsToOhifMessage) => void } {
  const navigate = useNavigate()
  const callbacksRef = useRef(callbacks)
  callbacksRef.current = callbacks

  const handleViewerMessage = useCallback(
    (msg: OhifToPmsMessage): void => {
      switch (msg.type) {
        case 'viewer-closed':
          navigate('/image-xrayviewer')
          break
        case 'check-previous-xrays':
          navigate('/image-xrayviewer')
          break
        case 'view-type-changed':
          break
        case 'open-notes-for-image': {
          const payload = (msg as OpenNotesForImageEvent).payload
          navigate('/notes', {
            state: {
              patientId: payload.patientId || undefined,
              contextType: NoteContextType.FILES,
              contextName: payload.imageFilename || 'X-ray Image',
              initialTitle: payload.suggestedTitle || undefined,
              openFirstNote: true,
              previousPage: { path: '/image-xrayviewer' },
            },
          })
          break
        }
        case 'open-cloud-storage':
          callbacksRef.current?.onOpenCloudStorage?.()
          break
        case 'print-report':
          callbacksRef.current?.onPrintReport?.()
          break
        case 'add-files':
          callbacksRef.current?.onAddFiles?.()
          break
        case 'delete-file-request':
          callbacksRef.current?.onDeleteFileRequest?.(
            (msg as DeleteFileRequestEvent).payload,
          )
          break
        case 'request-file-metadata':
          callbacksRef.current?.onRequestFileMetadata?.()
          break
        case 'report-submitted':
        case 'annotation-saved':
          break
        default:
          break
      }
    },
    [navigate],
  )

  useEffect(() => {
    const onMessage = (event: MessageEvent): void => {
      const ohifOrigin = new URL(ohifUrl).origin
      if (event.origin !== ohifOrigin) {
        // Helps diagnose origin mismatches between PMS and the embedded OHIF
        // viewer. Only log when the payload looks like one of ours so we
        // don't spam the console with unrelated messages.
        if (
          event.data &&
          typeof event.data === 'object' &&
          (event.data as { source?: unknown }).source ===
            IMAGE_VIEWER_MESSAGE_SOURCE
        ) {
          // eslint-disable-next-line no-console
          console.warn(
            '[PMS-meta][PMS] dropping viewer message — origin mismatch',
            { eventOrigin: event.origin, expectedOrigin: ohifOrigin },
          )
        }
        return
      }
      if (!isImageViewerMessageEnvelope(event.data)) return
      // eslint-disable-next-line no-console
      console.info(
        '[PMS-meta][PMS] received viewer message',
        (event.data.message as OhifToPmsMessage).type,
      )
      handleViewerMessage(event.data.message as OhifToPmsMessage)
    }

    window.addEventListener('message', onMessage)
    return () => window.removeEventListener('message', onMessage)
  }, [ohifUrl, handleViewerMessage])

  const sendCommand = useCallback(
    (message: PmsToOhifMessage): void => {
      const targetWindow = iframeRef.current?.contentWindow
      if (!targetWindow) return
      const ohifOrigin = new URL(ohifUrl).origin
      targetWindow.postMessage(
        { source: IMAGE_VIEWER_MESSAGE_SOURCE, message },
        ohifOrigin,
      )
    },
    [ohifUrl, iframeRef],
  )

  return { sendCommand }
}

function useBeforeUnloadGuard(active: boolean): void {
  useEffect(() => {
    if (!active) return
    const handler = (e: BeforeUnloadEvent): void => {
      e.preventDefault()
    }
    window.addEventListener('beforeunload', handler)
    return () => window.removeEventListener('beforeunload', handler)
  }, [active])
}

/**
 * Auto-detects whether a set of DICOM files represents a panoramic or FMX study.
 * - 10+ intraoral series or study description containing "FMX"/"full mouth" → 'fmx'
 * - Study description containing "panoramic"/"pano"/"opg" → 'panoramic'
 * - Default → 'panoramic'
 */
function detectStudyType(
  dicomFiles: Array<{
    study_description?: string
    series_description?: string
    modality?: string
    [key: string]: any
  }>,
): 'panoramic' | 'fmx' {
  if (!dicomFiles || dicomFiles.length === 0) return 'panoramic'

  const descriptions = dicomFiles
    .map((f) =>
      [f.study_description || '', f.series_description || '']
        .join(' ')
        .toLowerCase(),
    )
    .join(' ')

  if (/\bfmx\b|full\s*mouth/.test(descriptions)) {
    return 'fmx'
  }

  const ioCount = dicomFiles.filter(
    (f) =>
      f.modality === 'IO' ||
      /periapical|bitewing|\bpa\b|\bbw\b/i.test(f.series_description || ''),
  ).length

  if (ioCount >= 10) {
    return 'fmx'
  }

  if (/panoramic|pano\b|opg/i.test(descriptions)) {
    return 'panoramic'
  }

  return 'panoramic'
}

interface DicomNavFile {
  id: string
  fileName: string
  originalName?: string
  studyUid?: string
  seriesUid?: string
  sopUid?: string
}

interface XrayViewerLocationState {
  dicomFiles?: DicomNavFile[]
}

// sessionStorage key prefix used to persist the most recent nav-seeded
// file metadata across viewer page refreshes / HMR remounts. The router
// state is read once on mount and then cleared (so a refresh resets layout
// hints) — without this fallback, a refresh would lose the filename hint
// and OHIF would label the thumbnail with a generic StudyDescription.
//
// The seed is **scoped by patient_id** so entries from one patient never
// leak into another patient's viewer. Earlier this seed was global, which
// meant the OHIF "show only PMS-tracked thumbnails" filter would treat a
// previous patient's series UIDs as known-good for whichever patient was
// currently open — producing the cross-patient thumbnail bleed users saw
// after creating a new patient and uploading a file.
const XRAY_VIEWER_SEED_STORAGE_PREFIX = 'xrayViewer.recentlyUploadedFiles.seed'

const buildSeedStorageKey = (patientId: string | null | undefined): string =>
  patientId
    ? `${XRAY_VIEWER_SEED_STORAGE_PREFIX}.${patientId}`
    : XRAY_VIEWER_SEED_STORAGE_PREFIX

const readSeedFromStorage = (
  patientId: string | null | undefined,
): Array<{
  file_name: string
  original_name: string
  study_uid: string | null
  series_uid: string | null
  sop_uid: string | null
}> => {
  try {
    const cached = sessionStorage.getItem(buildSeedStorageKey(patientId))
    if (!cached) return []
    const parsed = JSON.parse(cached)
    return Array.isArray(parsed) ? parsed : []
  } catch {
    return []
  }
}

function ImageXrayViewer(): React.ReactElement {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const location = useLocation()
  const iframeRef = useRef<HTMLIFrameElement>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const dropZoneRef = useRef<HTMLDivElement>(null)

  const [showReportModal, setShowReportModal] = useState(false)
  const [showCloudPicker, setShowCloudPicker] = useState(false)
  const [isDragging, setIsDragging] = useState(false)
  const [uploading, setUploading] = useState(false)
  const [uploadProgress, setUploadProgress] = useState(0)
  const [showUpload, setShowUpload] = useState(false)
  const [validationErrors, setValidationErrors] = useState<string[]>([])
  const [uploadedStudyUids, setUploadedStudyUids] = useState<
    { studyUid: string; seriesUid: string }[] | null
  >(null)
  // The most-recently uploaded study/series. When set, an effect repeatedly
  // posts a `navigate-to-study` command to the OHIF iframe so the main
  // viewport switches to the file the user just added instead of remaining
  // on whichever study was previously selected. Cleared a few seconds later
  // once the iframe has had time to honour the command.
  const [pendingNavigationTarget, setPendingNavigationTarget] = useState<{
    studyUid: string
    seriesUid: string
    requestedAt: number
  } | null>(null)
  // In-memory cache of files that have been uploaded during this session.
  // We push directly from the upload response (`result` IFile) so OHIF can
  // resolve a real filename for the new thumbnail even before the redux
  // `fetchPatientFolder` refresh has reflected the new row. Indexed by
  // (study|series|sop) UID, so that the OHIF-side resolver — which can match
  // by any of those UIDs — finds the entry no matter which the viewer hands
  // us first.
  const [recentlyUploadedFiles, setRecentlyUploadedFiles] = useState<
    Array<{
      file_name: string
      original_name: string
      study_uid: string | null
      series_uid: string | null
      sop_uid: string | null
    }>
  >([])
  // Resolved separately in an effect once `selectedPatient` is known so the
  // seed is scoped per-patient. We can't do this in the useState initialiser
  // because we don't know which patient is active yet at module-mount time
  // (selectedPatient comes from redux which may still be hydrating).
  const [deleteConfirm, setDeleteConfirm] = useState<{
    show: boolean
    fileId: string | null
    fileName: string
    seriesUid: string | null
    studyUid: string | null
  }>({
    show: false,
    fileId: null,
    fileName: '',
    seriesUid: null,
    studyUid: null,
  })
  const { showToast } = useNotificationToast()
  const { showLoading, hideLoading } = useGlobalLoadingActions()

  const token = useAppSelector((state) => state.auth.accessToken)
  const selectedPatient = useAppSelector(
    (state) => state.patient.selectedPatient,
  )
  const selectedClinic = useAppSelector((state) => state.clinic.selectedClinic)
  const { files, loading, selectedFolder, childFolders } = useAppSelector(
    (state) => state.folder,
  )

  // Publish the open patient to this workstation's selected chair so DICOM
  // images captured at that operatory route to this patient (active-patient
  // routing). No-ops until a chair is chosen via the picker below.
  usePublishActivePatient(selectedPatient?.patient_id ?? null)

  const xrayFolderId = useMemo(() => {
    const xrayFolder = childFolders.find(
      (f) => f.folder_name.toLowerCase() === 'x-ray files',
    )
    return xrayFolder?.folder_id ?? selectedFolder?.folder_id
  }, [childFolders, selectedFolder])

  // Snapshot the incoming navigation state on first mount only. `location.state`
  // is persisted by the browser in `window.history.state` across refreshes, so
  // without this snapshot a page refresh would keep re-applying the multi-
  // viewport layout hint that was only meant for the initial navigation. We
  // capture it once with `useState(initial)` and then clear it from history so
  // a subsequent refresh starts clean (default single viewport).
  const [navState] = useState<XrayViewerLocationState | null>(
    () => (location.state as XrayViewerLocationState | null) ?? null,
  )

  useEffect(() => {
    if (location.state) {
      navigate(location.pathname, { replace: true, state: null })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const OHIF_URL = getEnv('REACT_APP_OHIF_URL', 'http://localhost:3000')

  // Tracks the user-selected study/series pairs that should drive the
  // multi-viewport layout hint (one viewport per file). Seeded from the
  // initial route state (when the user enters via the Files page →
  // "View with DICOM") and overwritten when the user picks a fresh set
  // of files from the in-page cloud-storage picker modal. Stored as
  // state — not derived solely from `navState` — so subsequent picks
  // from the modal can refresh the layout without a real navigation.
  const [selectedStudyUids, setSelectedStudyUids] = useState<
    { studyUid: string; seriesUid: string }[] | null
  >(() => {
    if (!navState?.dicomFiles) return null
    const uids = navState.dicomFiles
      .filter((f) => f.studyUid && f.seriesUid)
      .map((f) => ({ studyUid: f.studyUid!, seriesUid: f.seriesUid! }))
    return uids.length > 0 ? uids : null
  })
  const navStudyUids = selectedStudyUids

  const allDicomFiles = useMemo(() => {
    const rootFiles = files || []
    const childFiles = childFolders.flatMap((cf) => cf.files || [])
    const dicoms = [...rootFiles, ...childFiles].filter(
      (f) => f.is_dicom && f.study_uid && f.series_uid,
    )
    // Sort by upload time, newest first, so the OHIF Images panel lists the
    // most recently uploaded file at the top.
    return dicoms.sort((a, b) => {
      const aTime = a.date_time_created_at
        ? new Date(a.date_time_created_at).getTime()
        : 0
      const bTime = b.date_time_created_at
        ? new Date(b.date_time_created_at).getTime()
        : 0
      return bTime - aTime
    })
  }, [files, childFolders])

  // Always show every DICOM file that belongs to the patient in the left
  // panel — regardless of whether the user entered the viewer by clicking a
  // specific file on the /files page. `navStudyUids` is still honoured for
  // *initial viewport selection* (via `baseUids` below) but must not be
  // used to filter the overall panel list, otherwise entering from the
  // Files page would only ever show a single thumbnail.
  const dicomFiles = allDicomFiles
  const hasDicomFiles =
    dicomFiles.length > 0 ||
    (uploadedStudyUids !== null && uploadedStudyUids.length > 0)

  // Always default to single-viewport (panoramic) on load. Users can opt into
  // FMX multi-viewport layout via the "FMX View" toggle inside OHIF itself.
  const [viewType] = useState<'panoramic' | 'fmx'>('panoramic')

  const allDicomFilesRef = useRef(allDicomFiles)
  allDicomFilesRef.current = allDicomFiles

  const handleDeleteFileRequest = useCallback(
    (payload: DeleteFileRequestEvent['payload']) => {
      const { seriesInstanceUID, studyInstanceUID, imageFilename } = payload
      const list = allDicomFilesRef.current
      const match =
        list.find((f) => f.series_uid === seriesInstanceUID) ||
        list.find((f) => f.study_uid === studyInstanceUID)

      if (!match) {
        // No PMS file record matches — likely orphaned data in the DICOM
        // store (e.g. the PMS DB was reseeded but Azure DICOM still holds
        // the old studies). Offer to delete from the DICOM store directly.
        console.warn(
          '[XrayViewer] No PMS file matched DICOM series',
          { seriesInstanceUID, studyInstanceUID },
          'Known series:',
          list.map((f) => f.series_uid),
        )
        setDeleteConfirm({
          show: true,
          fileId: null,
          fileName: imageFilename || 'this orphaned study',
          seriesUid: seriesInstanceUID,
          studyUid: studyInstanceUID || null,
        })
        return
      }
      setDeleteConfirm({
        show: true,
        fileId: match.file_id,
        fileName:
          imageFilename ||
          match.original_name ||
          match.file_name ||
          'this file',
        seriesUid: seriesInstanceUID,
        studyUid: match.study_uid || studyInstanceUID || null,
      })
    },
    [],
  )

  // Keep a ref to the latest dicomFiles so the message-bridge callback can
  // post fresh metadata in response to OHIF's `request-file-metadata` events
  // without re-subscribing the bridge each time the list changes.
  const dicomFilesRef = useRef(dicomFiles)
  dicomFilesRef.current = dicomFiles

  const recentlyUploadedFilesRef = useRef(recentlyUploadedFiles)
  recentlyUploadedFilesRef.current = recentlyUploadedFiles

  const sendCommandRef = useRef<(msg: PmsToOhifMessage) => void>(
    () => undefined,
  )

  // Accumulate every StudyInstanceUID we have ever seen for the current
  // patient. The redux folder slice can briefly empty out while a
  // `fetchPatientFolder` round-trip is in flight (or when the user enters
  // via /files → "View with DICOM" — at that moment redux is still empty
  // and `navStudyUids` only carries the single clicked study). Using just
  // the latest snapshot would build the iframe URL with one study, lock
  // OHIF onto a single thumbnail, and never recover even after the redux
  // refresh fills in the remaining files. The accumulator keeps every UID
  // we have observed since the patient was selected, so the iframe URL —
  // and its remount key — always reflects the *union* of known studies.
  // It is reset only when the patient actually changes. Declared here
  // (above the early-return for the upload screen) so the hook order
  // remains stable across renders.
  const studyUidAccumulatorRef = useRef<{
    patientId: string | null
    uids: Set<string>
  }>({ patientId: null, uids: new Set() })

  // Bumped on every successful delete so the OHIF iframe is forced to
  // remount even when the URL's StudyInstanceUIDs list happens to be
  // identical (e.g. deleting one of several series within the same study).
  // Without this, OHIF can hold on to its stale "study not available"
  // fallback after a deletion.
  const [deleteVersion, setDeleteVersion] = useState(0)
  // Bumped on every cloud-storage pick so the OHIF iframe re-mounts and
  // re-applies the current `layout` hint even when the picked studies were
  // already loaded (and the StudyInstanceUIDs portion of the URL is
  // unchanged). Without this, picking 4 files from the modal after the
  // viewer was already showing those 4 studies in 1 viewport would never
  // refresh the multi-viewport layout.
  const [selectionVersion, setSelectionVersion] = useState(0)

  const sendFileMetadata = useCallback((): void => {
    // Combine redux-derived files with in-session upload results. The latter
    // covers the gap between `uploadFile` returning and `fetchPatientFolder`
    // refreshing the patient tree — without it, OHIF can request metadata
    // and get an empty payload while the viewer is already showing the new
    // thumbnail.
    const reduxList = dicomFilesRef.current || []
    const recent = recentlyUploadedFilesRef.current || []

    type Pick = {
      file_name: string | null | undefined
      original_name: string | null | undefined
      study_uid: string | null | undefined
      series_uid: string | null | undefined
      sop_uid: string | null | undefined
    }
    const merged: Pick[] = [...reduxList, ...recent]
    if (merged.length === 0) {
      // eslint-disable-next-line no-console
      console.info(
        '[PMS-meta][PMS] sendFileMetadata skipped - no dicom files yet',
      )
      return
    }

    // De-dupe by series_uid → sop_uid → file_name so a row that lands in
    // both lists (post-refetch) doesn't generate two payload entries.
    const seen = new Set<string>()
    const filesMeta: Array<{
      seriesUID: string
      fileName: string
      originalName: string
      studyUID?: string
      sopUID?: string
    }> = []
    merged.forEach((f) => {
      if (!f.series_uid) return
      const dedupeKey = f.series_uid || f.sop_uid || f.file_name || ''
      if (!dedupeKey || seen.has(dedupeKey)) return
      seen.add(dedupeKey)
      filesMeta.push({
        seriesUID: f.series_uid,
        fileName: f.file_name || '',
        originalName: f.original_name || f.file_name || '',
        studyUID: f.study_uid || undefined,
        sopUID: f.sop_uid || undefined,
      })
    })

    if (filesMeta.length === 0) {
      // eslint-disable-next-line no-console
      console.info(
        '[PMS-meta][PMS] sendFileMetadata skipped - files have no series_uid',
      )
      return
    }
    // eslint-disable-next-line no-console
    console.info('[PMS-meta][PMS] sending set-file-metadata', filesMeta)
    sendCommandRef.current({
      type: 'set-file-metadata',
      payload: { files: filesMeta },
    })
  }, [])

  const { sendCommand } = useXrayViewerBridge(iframeRef, OHIF_URL, {
    onPrintReport: () => setShowReportModal(true),
    onAddFiles: () => fileInputRef.current?.click(),
    onOpenCloudStorage: () => setShowCloudPicker(true),
    onDeleteFileRequest: handleDeleteFileRequest,
    // OHIF requests fresh metadata whenever its iframe (re)loads. Reply
    // immediately so the viewer never has to rely on an in-flight broadcast
    // landing during its brief mounting window.
    onRequestFileMetadata: () => sendFileMetadata(),
  })
  sendCommandRef.current = sendCommand
  useBeforeUnloadGuard(hasDicomFiles)

  useEffect(() => {
    if (dicomFiles.length === 0 && recentlyUploadedFiles.length === 0) return

    // Repeatedly push file metadata into the OHIF iframe for the first few
    // seconds after mount or after a new upload. This handles two race
    // conditions:
    //   1. The iframe's message listener may not yet be registered when the
    //      first message is posted.
    //   2. If the iframe reloads (e.g. user navigates inside the viewer),
    //      its in-memory seriesUID → filename map is cleared.
    // The OHIF side applies this idempotently, so repeated sends are safe.
    // OHIF *also* asks for metadata on (re)load via `request-file-metadata`,
    // which is the primary delivery path; this interval just covers the
    // rare case where that request is missed.
    sendFileMetadata()
    const interval = setInterval(sendFileMetadata, 500)
    const stopTimer = setTimeout(() => clearInterval(interval), 5000)

    return () => {
      clearInterval(interval)
      clearTimeout(stopTimer)
    }
  }, [dicomFiles, recentlyUploadedFiles, sendFileMetadata])

  // After a successful upload, tell the OHIF iframe to switch its main
  // viewport to the newly uploaded study. The iframe may remount when the
  // StudyInstanceUID list changes, so we send a few spaced commands — not a
  // tight interval — to avoid stacking OHIF listeners while still catching
  // late iframe readiness.
  useEffect(() => {
    if (!pendingNavigationTarget) return
    const { studyUid, seriesUid } = pendingNavigationTarget
    const send = (): void =>
      sendCommandRef.current({
        type: 'navigate-to-study',
        payload: {
          studyInstanceUID: studyUid,
          seriesInstanceUID: seriesUid,
        },
      })
    send()
    const t1 = window.setTimeout(send, 900)
    const t2 = window.setTimeout(send, 2200)
    const t3 = window.setTimeout(send, 4500)
    const clearTarget = window.setTimeout(() => {
      setPendingNavigationTarget(null)
    }, 5500)
    return () => {
      window.clearTimeout(t1)
      window.clearTimeout(t2)
      window.clearTimeout(t3)
      window.clearTimeout(clearTarget)
    }
  }, [pendingNavigationTarget])

  useEffect(() => {
    if (selectedPatient?.patient_id) {
      dispatch(fetchPatientFolder(selectedPatient.patient_id))
    }
  }, [dispatch, selectedPatient?.patient_id, selectedClinic?.clinic_id])

  // Realtime auto-load: when a DICOM capture device / simulator C-STOREs a new
  // image for the patient currently open in the viewer, the dicom-ingest
  // pipeline broadcasts a `dicom.ingested` event over the notifications socket.
  // Refetching the patient folder feeds the new study UID into the same merge
  // logic used after a manual upload (effectiveUids → iframe remount + OHIF
  // metadata push), so the new x-ray appears without a full page refresh.
  useEffect(() => {
    const patientId = selectedPatient?.patient_id
    if (!patientId) return
    const socket = getNotificationsSocket()
    if (!socket) return

    const handleDicomIngested = (payload: {
      patient_id?: string
      study_uid?: string | null
      series_uid?: string | null
    }): void => {
      if (!payload?.patient_id || payload.patient_id !== patientId) return
      dispatch(fetchPatientFolder(patientId))
      if (payload.study_uid && payload.series_uid) {
        setPendingNavigationTarget({
          studyUid: payload.study_uid,
          seriesUid: payload.series_uid,
          requestedAt: Date.now(),
        })
      }
    }

    socket.on('dicom.ingested', handleDicomIngested)
    return () => {
      socket.off('dicom.ingested', handleDicomIngested)
    }
  }, [dispatch, selectedPatient?.patient_id])

  // Reset per-session caches when the patient changes. Both
  // `recentlyUploadedFiles` (sent to OHIF as PMS file metadata) and
  // `uploadedStudyUids` (merged into the iframe URL's StudyInstanceUIDs)
  // are scoped to a single patient; without this reset they would leak
  // a previous patient's series UIDs into the new patient's viewer, and
  // the OHIF panel — which now trusts the PMS metadata as the source of
  // truth for which thumbnails to render — would happily display them.
  //
  // The seed for the *active* patient is reloaded from sessionStorage
  // (so a hard refresh or HMR remount still recovers filename hints for
  // files just uploaded in this tab) and merged with any nav-state hints
  // passed in from /files → "View with DICOM".
  useEffect(() => {
    const activePatientId = selectedPatient?.patient_id ?? null
    type SeedEntry = {
      file_name: string
      original_name: string
      study_uid: string | null
      series_uid: string | null
      sop_uid: string | null
    }

    const fromStorage: SeedEntry[] = readSeedFromStorage(activePatientId)
    const navDicom = (location.state as XrayViewerLocationState | null)
      ?.dicomFiles
    const fromNav: SeedEntry[] = (navDicom ?? [])
      .filter((f) => f.seriesUid || f.studyUid || f.sopUid)
      .map((f) => ({
        file_name: f.fileName,
        original_name: f.originalName || f.fileName,
        study_uid: f.studyUid || null,
        series_uid: f.seriesUid || null,
        sop_uid: f.sopUid || null,
      }))

    const merged: SeedEntry[] = []
    const seen = new Set<string>()
    const pushUnique = (e: SeedEntry): void => {
      const key =
        e.series_uid || e.sop_uid || `${e.study_uid ?? ''}|${e.file_name}`
      if (!key || seen.has(key)) return
      seen.add(key)
      merged.push(e)
    }
    fromNav.forEach(pushUnique)
    fromStorage.forEach(pushUnique)

    setRecentlyUploadedFiles(merged)
    setUploadedStudyUids(null)

    if (activePatientId && merged.length > 0) {
      try {
        sessionStorage.setItem(
          buildSeedStorageKey(activePatientId),
          JSON.stringify(merged),
        )
      } catch {
        // sessionStorage may be unavailable (private mode); ignore.
      }
    }
    // location.state is only consumed once on entry; subsequent patient
    // switches re-run this with whatever nav-state happens to be present
    // (usually nothing), which is the correct behaviour.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPatient?.patient_id])

  const handleUpload = useCallback(
    async (fileList: FileList) => {
      if (!selectedPatient?.patient_id || fileList.length === 0) return

      // Build a case-insensitive set of full filenames (name + extension)
      // already present for this patient so we can reject duplicate uploads
      // before sending them to the server.
      const existingNames = new Set<string>()
      const collectName = (name: string | null | undefined): void => {
        if (name) existingNames.add(name.trim().toLowerCase())
      }
      ;(files || []).forEach((f) => {
        collectName(f.original_name)
        collectName(f.file_name)
      })
      childFolders.forEach((cf) =>
        (cf.files || []).forEach((f) => {
          collectName(f.original_name)
          collectName(f.file_name)
        }),
      )
      recentlyUploadedFilesRef.current.forEach((f) => {
        collectName(f.original_name)
        collectName(f.file_name)
      })

      const errors: string[] = []
      const validFiles: File[] = []
      const seenInBatch = new Set<string>()
      for (let i = 0; i < fileList.length; i++) {
        const file = fileList[i]
        const lowerName = file.name.trim().toLowerCase()

        if (existingNames.has(lowerName)) {
          errors.push(
            `"${file.name}" already exists. A file with the same name has already been uploaded.`,
          )
          continue
        }
        if (seenInBatch.has(lowerName)) {
          errors.push(
            `"${file.name}" is selected more than once. Duplicate filenames are not allowed.`,
          )
          continue
        }

        const error = validateFile(file)
        if (error) {
          errors.push(error)
        } else {
          seenInBatch.add(lowerName)
          validFiles.push(file)
        }
      }

      setValidationErrors(errors)
      if (errors.length > 0) {
        errors.forEach((message) =>
          showToast(createFailedToast('Upload Failed', message)),
        )
      }
      if (validFiles.length === 0) return

      setUploading(true)
      setUploadProgress(0)
      showLoading({ container: XRAY_VIEWER_LOADING_CONTAINER })

      const totalBytes = validFiles.reduce((sum, f) => sum + f.size, 0)
      let uploadedBytes = 0

      try {
        const newUids: { studyUid: string; seriesUid: string }[] = []
        for (let i = 0; i < validFiles.length; i++) {
          const file = validFiles[i]
          const fileStartBytes = uploadedBytes

          const result = await dispatch(
            uploadFileAction({
              file,
              patientId: selectedPatient.patient_id,
              folderId: xrayFolderId,
              onUploadProgress: (evt) => {
                const currentTotal = fileStartBytes + evt.loaded
                const pct =
                  totalBytes > 0
                    ? Math.min(
                        Math.round((currentTotal / totalBytes) * 100),
                        99,
                      )
                    : 0
                setUploadProgress(pct)
              },
            }),
          ).unwrap()

          uploadedBytes += file.size
          if (result?.study_uid && result?.series_uid) {
            newUids.push({
              studyUid: result.study_uid,
              seriesUid: result.series_uid,
            })
          }
          // Cache the upload result so OHIF can resolve a filename for the
          // new thumbnail even before the patient-folder refetch lands.
          if (result?.series_uid || result?.sop_uid || result?.study_uid) {
            const newEntry = {
              file_name: result.file_name || file.name,
              original_name:
                result.original_name || result.file_name || file.name,
              study_uid: result.study_uid || null,
              series_uid: result.series_uid || null,
              sop_uid: result.sop_uid || null,
            }
            setRecentlyUploadedFiles((prev) => {
              const next = [...prev, newEntry]
              try {
                sessionStorage.setItem(
                  buildSeedStorageKey(selectedPatient?.patient_id),
                  JSON.stringify(next),
                )
              } catch {
                // ignore storage errors
              }
              return next
            })
          }
        }

        setUploadProgress(100)
        dispatch(fetchPatientFolder(selectedPatient.patient_id))

        if (newUids.length > 0) {
          setUploadedStudyUids((prev) => {
            const existing = prev ?? []
            const merged = [...existing, ...newUids]
            const seen = new Set<string>()
            return merged.filter((u) => {
              const key = `${u.studyUid}|${u.seriesUid}`
              if (seen.has(key)) return false
              seen.add(key)
              return true
            })
          })
        }

        if (newUids.length > 0) {
          const latest = newUids[newUids.length - 1]
          setPendingNavigationTarget({
            studyUid: latest.studyUid,
            seriesUid: latest.seriesUid,
            requestedAt: Date.now(),
          })
        }

        setValidationErrors([])
        setShowUpload(false)
        showToast(
          createSuccessToast(
            'Upload Complete',
            `${newUids.length} file${newUids.length !== 1 ? 's' : ''} uploaded successfully.`,
          ),
        )
      } catch (err: any) {
        const raw = typeof err === 'string' ? err : err?.message || ''

        let title = 'Upload Failed'
        let message = raw || 'An unexpected error occurred during upload.'

        if (
          raw.toLowerCase().includes('already exists') ||
          raw.toLowerCase().includes('duplicate')
        ) {
          title = 'Duplicate File'
          message =
            'This DICOM file has already been uploaded. The existing study will be used.'
        } else if (raw.toLowerCase().includes('conflict')) {
          title = 'Upload Conflict'
          message =
            'A conflict occurred while uploading. Please try again or contact support.'
        }

        showToast(createFailedToast(title, message))
      } finally {
        setUploading(false)
        setUploadProgress(0)
        hideLoading(XRAY_VIEWER_LOADING_CONTAINER)
      }
    },
    [
      dispatch,
      selectedPatient?.patient_id,
      xrayFolderId,
      showToast,
      showLoading,
      hideLoading,
      files,
      childFolders,
    ],
  )

  const handleFileInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (e.target.files) {
        handleUpload(e.target.files)
      }
      if (fileInputRef.current) {
        fileInputRef.current.value = ''
      }
    },
    [handleUpload],
  )

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(true)
  }, [])

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(false)
  }, [])

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault()
      e.stopPropagation()
      setIsDragging(false)
      if (e.dataTransfer.files) {
        handleUpload(e.dataTransfer.files)
      }
    },
    [handleUpload],
  )

  /**
   * Apply files selected from the cloud-storage picker modal as if they were
   * just uploaded — seed PMS metadata cache, merge their study UIDs into the
   * iframe URL, and ask OHIF to focus the latest one. This mirrors the path
   * previously triggered by navigating to /files → "Open" → back to viewer,
   * but stays on the current URL so the user never leaves the X-ray viewer.
   */
  const handleCloudFilesSelected = useCallback(
    (selected: MockFileItem[]): void => {
      type SeedEntry = {
        file_name: string
        original_name: string
        study_uid: string | null
        series_uid: string | null
        sop_uid: string | null
      }
      // The Files picker resolves JPG/PNG to their converted .dcm before
      // calling back (see `convertImagesToDicom` in Files.page.tsx). Any
      // entry that still lacks DICOM UIDs at this point failed conversion
      // and should be reported, not silently dropped.
      const dicomSelected = selected.filter((f) => {
        const studyUid = f.studyUid ?? f.study_uid ?? undefined
        const seriesUid = f.seriesUid ?? f.series_uid ?? undefined
        return Boolean(studyUid && seriesUid)
      })
      const failedCount = selected.length - dicomSelected.length
      if (dicomSelected.length === 0) {
        showToast(
          createFailedToast(
            'No DICOM Files',
            'None of the selected files could be opened as DICOM. Please try again or upload the files directly.',
          ),
        )
        return
      }
      if (failedCount > 0) {
        showToast(
          createFailedToast(
            'Some Files Skipped',
            `${failedCount} file${failedCount === 1 ? '' : 's'} could not be converted to DICOM and were skipped.`,
          ),
        )
      }

      const newUids: { studyUid: string; seriesUid: string }[] = []
      const seedEntries: SeedEntry[] = []
      dicomSelected.forEach((f) => {
        const studyUid = (f.studyUid ?? f.study_uid) as string
        const seriesUid = (f.seriesUid ?? f.series_uid) as string
        const sopUid = f.sopUid ?? f.sop_uid ?? null
        newUids.push({ studyUid, seriesUid })
        seedEntries.push({
          file_name: f.fileName,
          original_name: f.originalName || f.fileName,
          study_uid: studyUid,
          series_uid: seriesUid,
          sop_uid: sopUid,
        })
      })

      setRecentlyUploadedFiles((prev) => {
        const merged = [...prev]
        const seen = new Set(
          prev
            .map((e) => e.series_uid || e.sop_uid || e.file_name)
            .filter(Boolean) as string[],
        )
        seedEntries.forEach((e) => {
          const key = (e.series_uid || e.sop_uid || e.file_name) ?? ''
          if (!key || seen.has(key)) return
          seen.add(key)
          merged.push(e)
        })
        try {
          sessionStorage.setItem(
            buildSeedStorageKey(selectedPatient?.patient_id),
            JSON.stringify(merged),
          )
        } catch {
          // ignore storage errors
        }
        return merged
      })

      setUploadedStudyUids((prev) => {
        const merged = [...(prev ?? []), ...newUids]
        const seen = new Set<string>()
        return merged.filter((u) => {
          const key = `${u.studyUid}|${u.seriesUid}`
          if (seen.has(key)) return false
          seen.add(key)
          return true
        })
      })

      // Drive the dental mode's multi-viewport layout hint by replacing the
      // selected set with whatever the user just picked. The layout hint
      // (`1x2`, `1x3`, `2x2`, ...) is derived from this list's length below
      // and appended to the iframe URL so OHIF opens one viewport per file.
      setSelectedStudyUids(newUids)

      // Only force a single-study focus when the user picked exactly one
      // file. For multi-select, let the multi-viewport hanging protocol
      // place the files itself.
      if (newUids.length === 1) {
        const only = newUids[0]
        setPendingNavigationTarget({
          studyUid: only.studyUid,
          seriesUid: only.seriesUid,
          requestedAt: Date.now(),
        })
      } else {
        setPendingNavigationTarget(null)
      }
      setSelectionVersion((v) => v + 1)
      setShowUpload(false)
    },
    [selectedPatient?.patient_id, showToast],
  )

  // FilesPickerModal must be rendered at a stable position across all
  // possible return paths below; if it moves between conditional branches
  // (e.g. empty-state → main viewer when files arrive) React unmounts and
  // re-mounts it on every state flip, causing visible blink/flicker. The
  // helper below wraps any body in a fragment alongside the modal so its
  // tree position is preserved across transitions.
  const renderWithCloudPicker = (body: React.ReactNode): React.ReactElement => (
    <div className="xray-viewer-page-root">
      <div className="xray-chair-picker-bar">
        <OperatoryChairPicker />
      </div>
      {body}
      <FilesPickerModal
        show={showCloudPicker}
        onHide={() => setShowCloudPicker(false)}
        onFilesSelected={handleCloudFilesSelected}
        initialOpenFolderName="X-Ray Files"
      />
    </div>
  )

  if (!selectedPatient) {
    return renderWithCloudPicker(
      <>
        <Container fluid className="border xray-viewer-container">
          <div className="xray-empty-state">
            <p className="text-muted">
              No patient selected. Please select a patient first.
            </p>
          </div>
        </Container>
      </>,
    )
  }

  // While the cloud-storage picker modal is open the embedded Files page
  // dispatches its own `fetchWorkspace` and flips the shared folder slice's
  // `loading` flag to true. Without this guard the viewer would swap to its
  // own loading screen mid-interaction.
  if (loading && !showCloudPicker) {
    return renderWithCloudPicker(
      <Container fluid className="border xray-viewer-container">
        <ScreenLoader
          open
          inline
          size="md"
          aria-label="Loading patient files"
        />
      </Container>,
    )
  }

  const hasNavUidsButNoReduxMatch =
    navStudyUids && navStudyUids.length > 0 && dicomFiles.length === 0
  const effectiveHasDicom = hasDicomFiles || hasNavUidsButNoReduxMatch

  if (!effectiveHasDicom || showUpload) {
    return renderWithCloudPicker(
      <>
        <Container fluid className="border xray-viewer-container">
          <div
            ref={dropZoneRef}
            className={`xray-empty-state xray-drop-zone ${isDragging ? 'xray-dragging' : ''}`}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
          >
            {!uploading && (
              <>
                <div className="xray-upload-icon">
                  <span className="mgc_file_upload_fill" />
                </div>
                <p className="xray-upload-text">
                  Drag and drop DICOM or image file here, or choose from
                </p>
                <div className="xray-upload-links">
                  <button
                    className="xray-link-btn"
                    onClick={() => fileInputRef.current?.click()}
                  >
                    local storage
                  </button>
                  <span className="xray-or-text">or</span>
                  <button
                    className="xray-link-btn"
                    onClick={() => setShowCloudPicker(true)}
                  >
                    cloud storage
                  </button>
                </div>
              </>
            )}

            {validationErrors.length > 0 && (
              <div className="xray-validation-errors">
                {validationErrors.map((err, i) => (
                  <p key={i} className="xray-validation-error">
                    {err}
                  </p>
                ))}
              </div>
            )}

            {uploading && (
              <div className="xray-upload-progress">
                <p className="xray-upload-progress-text">
                  Uploading... {uploadProgress}%
                </p>
                <div className="xray-progress-bar-track">
                  <div
                    className="xray-progress-bar-fill"
                    style={{ width: `${uploadProgress}%` }}
                  />
                </div>
              </div>
            )}

            <input
              ref={fileInputRef}
              type="file"
              accept=".dcm,application/dicom,.jpg,.jpeg,.png,image/jpeg,image/png"
              multiple
              style={{ display: 'none' }}
              onChange={handleFileInputChange}
            />
          </div>
        </Container>
      </>,
    )
  }

  // Load every DICOM file the patient has. We intentionally include all of
  // them (not just `navStudyUids`) so the OHIF Images panel lists every
  // available thumbnail even when the user entered the viewer by clicking a
  // single file on the /files page. If `navStudyUids` was supplied we merge
  // them in first so they appear at the top of the effective list.
  const baseUids = [
    ...(navStudyUids ?? []),
    ...dicomFiles.map((f) => ({
      studyUid: f.study_uid!,
      seriesUid: f.series_uid!,
    })),
  ]

  // Merge in any UIDs uploaded in this session so newly-added files appear
  // alongside the existing ones without requiring a page refresh. Newest
  // uploads go first so they render at the top of OHIF's Images panel.
  const effectiveUids = (() => {
    const merged: { studyUid: string; seriesUid: string }[] = []
    const seen = new Set<string>()
    const push = (uid: { studyUid: string; seriesUid: string }): void => {
      const key = `${uid.studyUid}|${uid.seriesUid}`
      if (seen.has(key)) return
      seen.add(key)
      merged.push(uid)
    }
    if (uploadedStudyUids) uploadedStudyUids.forEach(push)
    baseUids.forEach(push)
    return merged
  })()

  // Build the iframe URL using only distinct StudyInstanceUIDs. OHIF's
  // DICOMweb data source loads every series that belongs to a given study,
  // so passing the study UIDs is sufficient to show all DICOM files for the
  // patient. We deliberately avoid sending SeriesInstanceUIDs here: the
  // viewer would otherwise treat them as a series filter and, because its
  // DICOMweb client only honours a single SeriesInstanceUID at a time, only
  // one thumbnail would appear in the Images panel even when the patient
  // has many DICOM files.
  const reduxStudyUids = Array.from(
    new Set(effectiveUids.map((u) => u.studyUid)),
  )

  const currentPatientId = selectedPatient?.patient_id ?? null
  if (studyUidAccumulatorRef.current.patientId !== currentPatientId) {
    studyUidAccumulatorRef.current = {
      patientId: currentPatientId,
      uids: new Set(reduxStudyUids),
    }
  } else {
    reduxStudyUids.forEach((uid) =>
      studyUidAccumulatorRef.current.uids.add(uid),
    )
  }

  // Preserve insertion order from the latest snapshot first (so newest /
  // navigated studies stay at the top of OHIF's Images panel), then append
  // any earlier-seen studies that have since dropped out of redux.
  const uniqueStudyUids: string[] = []
  const seenForUrl = new Set<string>()
  reduxStudyUids.forEach((uid) => {
    if (!seenForUrl.has(uid)) {
      seenForUrl.add(uid)
      uniqueStudyUids.push(uid)
    }
  })
  studyUidAccumulatorRef.current.uids.forEach((uid) => {
    if (!seenForUrl.has(uid)) {
      seenForUrl.add(uid)
      uniqueStudyUids.push(uid)
    }
  })

  const queryParams = uniqueStudyUids
    .map((sid) => `StudyInstanceUIDs=${sid}`)
    .join('&')

  // iframe key uses ONLY the sorted set of unique StudyInstanceUIDs so that
  // gradually-arriving series within an already-loaded study don't churn the
  // iframe. Without sorting+uniquing, a Redux refresh that reorders entries
  // would cause a needless remount that interrupts the user's view.
  const iframeKey =
    (uniqueStudyUids.length > 0
      ? [...uniqueStudyUids].sort().join(',')
      : 'empty') + `|d${deleteVersion}|s${selectionVersion}`

  // When the user navigates in from the Files page with multiple files
  // selected, hint the OHIF dental mode to open a matching multi-viewport
  // layout so every selected series gets its own viewport. The hint is only
  // present while the navigation state is alive (fresh navigation from the
  // Files page) — after a browser refresh `navStudyUids` is null, so the
  // viewer falls back to its default single viewport.
  const selectedCount = navStudyUids?.length ?? 0
  const layoutHint =
    selectedCount >= 4
      ? '2x2'
      : selectedCount === 3
        ? '1x3'
        : selectedCount === 2
          ? '1x2'
          : null

  const handleBackToUpload = (): void => {
    setUploadedStudyUids(null)
    setShowUpload(true)
  }

  const handleConfirmDelete = async (): Promise<void> => {
    const { fileId, seriesUid, studyUid, fileName } = deleteConfirm
    // Close the confirmation modal immediately so it doesn't sit behind the
    // loading overlay while the delete request is in flight.
    setDeleteConfirm({
      show: false,
      fileId: null,
      fileName: '',
      seriesUid: null,
      studyUid: null,
    })
    showLoading({ container: XRAY_VIEWER_LOADING_CONTAINER })
    try {
      if (fileId) {
        await dispatch(deleteFileAction(fileId)).unwrap()
      } else if (studyUid) {
        // Orphan path: no PMS record exists for this DICOM (e.g. DB was
        // reseeded). Delete it directly from the DICOMweb service so it
        // stops appearing in the viewer.
        const result = await deleteOrphanDicomStudy(
          studyUid,
          seriesUid || undefined,
        )
        if (result.statusCode >= 300) {
          throw new Error(result.message)
        }
      } else {
        throw new Error('Missing identifiers to delete this file.')
      }

      if (seriesUid) {
        setUploadedStudyUids((prev) =>
          prev ? prev.filter((u) => u.seriesUid !== seriesUid) : prev,
        )
      }
      if (selectedPatient?.patient_id) {
        await dispatch(fetchPatientFolder(selectedPatient.patient_id))
      }

      // Prune the deleted study UID from the accumulator when no remaining
      // series belongs to it. The accumulator is otherwise additive, so
      // without this the deleted UID would stay in the iframe URL and OHIF
      // would render its stock "study not available" / study-list fallback.
      if (studyUid) {
        const remainingFiles = allDicomFilesRef.current || []
        const stillReferenced =
          remainingFiles.some(
            (f) =>
              f.study_uid === studyUid &&
              (!seriesUid || f.series_uid !== seriesUid),
          ) ||
          (uploadedStudyUids?.some(
            (u) =>
              u.studyUid === studyUid &&
              (!seriesUid || u.seriesUid !== seriesUid),
          ) ??
            false)
        if (!stillReferenced) {
          studyUidAccumulatorRef.current.uids.delete(studyUid)
        }
      }

      // Force the OHIF iframe to remount via key change so it picks up the
      // new StudyInstanceUIDs list and never gets stuck on its stock
      // "study not available" fallback (which links to OHIF's default
      // /study-list page).
      setDeleteVersion((v) => v + 1)
      showToast(createSuccessToast('File Deleted', `${fileName} was removed.`))
    } catch (err: any) {
      const message =
        typeof err === 'string'
          ? err
          : err?.message || 'Failed to delete the file.'
      showToast(createFailedToast('Delete Failed', message))
    } finally {
      hideLoading(XRAY_VIEWER_LOADING_CONTAINER)
    }
  }

  const handleCancelDelete = (): void => {
    setDeleteConfirm({
      show: false,
      fileId: null,
      fileName: '',
      seriesUid: null,
      studyUid: null,
    })
  }

  return renderWithCloudPicker(
    <>
      <Container fluid className="border xray-viewer-container">
        <button
          className="xray-back-btn"
          onClick={handleBackToUpload}
          title="Back to upload"
          aria-label="Back to upload"
        >
          <span className="xray-back-arrow">&#8592;</span>
        </button>
        <iframe
          key={iframeKey}
          ref={iframeRef}
          src={`${OHIF_URL}/dental?${queryParams}&token=${token}&patient_id=${selectedPatient.patient_id}&folder_id=${xrayFolderId || ''}&upload_url=${encodeURIComponent(getEnv('REACT_APP_BUSINESS_LAYER_FOLDER', 'http://localhost:3015'))}&viewType=${viewType}${layoutHint ? `&layout=${layoutHint}` : ''}&chromeless=0`}
          className="xray-viewer-iframe"
          allow="cross-origin-isolated"
          sandbox="allow-same-origin allow-scripts allow-forms allow-downloads"
        />
        <input
          ref={fileInputRef}
          type="file"
          accept=".dcm,application/dicom,.jpg,.jpeg,.png,image/jpeg,image/png"
          multiple
          style={{ display: 'none' }}
          onChange={handleFileInputChange}
        />
      </Container>
      <ImageViewerModal
        show={showReportModal}
        handleClose={() => setShowReportModal(false)}
        patient={selectedPatient}
        clinic={selectedClinic}
        dicomFiles={dicomFiles}
      />
      <ConfirmDeleteModal
        show={deleteConfirm.show}
        onHide={handleCancelDelete}
        onConfirm={handleConfirmDelete}
        entityName={deleteConfirm.fileName}
        title={
          deleteConfirm.fileId
            ? 'Delete X-Ray File'
            : 'Delete Orphan DICOM Study'
        }
        message={
          deleteConfirm.fileId
            ? 'Are you sure you want to delete'
            : 'No matching PMS record was found (the database may have been reseeded). Delete this study from the DICOM service for'
        }
        confirmButtonText="Delete"
      />
    </>,
  )
}

export default ImageXrayViewer
